// Angular Files
import {
 Component, EventEmitter, Input, Output, HostBinding, ViewEncapsulation
} from '@angular/core';

// Teller Online Library Files
import { TellerOnlineAppService } from 'libraries/core/src/lib/services/app.service';
import { TellerOnlineSiteMetadataService } from 'libraries/core/src/lib/services/site-metadata.service';
import { MenuItemDto } from 'libraries/core/src/lib/api/CoreWebApi';

// Service interfaces for optional injection
export interface AuthServiceInterface {
    isSignedIn: boolean;
    signOut(redirectUrl?: string): Promise<void>;
}

export interface UserServiceInterface {
    currentUser?: {
        name: string;
    };
}

export interface InboundRedirectServiceInterface {
    isInboundRedirectUIContext: boolean;
    redirectSourceFriendlyName: string;
    promptForRedirectToSource(): Promise<void>;
    cancelPaymentAndRedirect(): Promise<void>;
}

export interface PaymentProcessorProviderInterface {
    canSavePaymentMethod: boolean;
}

export interface HeaderEditEvent {
    field: string;
    value: string;
}

@Component({
    selector: 'public-header',
    templateUrl: './public-portal-header.component.html',
    styleUrls: ['./public-portal-header.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class PublicPortalHeaderComponent {
    @Input() preview: boolean = false;

    // Single-owner rule for the 'header' band class (mirrors app-footer's FooterComponent):
    // LIVE, the app-header wrapper owns the band (host class 'header' + :host band styles), so this
    // inner host must NOT also carry 'header' or the band would render twice. In PREVIEW (admin site
    // editor) there is no app-header wrapper, so public-header IS the header element and owns 'header'.
    @HostBinding('class')
    get componentClass() {
        return this.preview ? 'header' : '';
    }

    @Input() siteTitle: string = '';

    @Input() mobileMenuOpened: boolean = false;

    @Input() helpOpened: boolean = false;

    @Input() cartItemCount: number = 0;

    @Input() cartOpened: boolean = false;

    @Input() accountMenuOpened: boolean = false;

    @Input() appService?: TellerOnlineAppService;

    @Input() siteMetadataService?: TellerOnlineSiteMetadataService;

    @Input() authService?: AuthServiceInterface;

    @Input() userService?: UserServiceInterface;

    @Input() paymentProvider?: PaymentProcessorProviderInterface;

    @Input() inboundRedirectService?: InboundRedirectServiceInterface;

    @Input() logoSrc?: string;

    @Input() authenticationEnabled?: boolean = this.siteMetadataService?.authenticationEnabled;

    // Edit mode inputs
    @Input() editMode: boolean = false;

    @Output() editChange = new EventEmitter<HeaderEditEvent>();

    @Output() toggleMobileMenu = new EventEmitter<boolean>();

    @Output() toggleHelp = new EventEmitter<boolean>();

    @Output() toggleCart = new EventEmitter<boolean>();

    @Output() toggleAccountMenu = new EventEmitter<boolean>();

    @Input() menuItems?: MenuItemDto[];

    @Input() returnToSenderLabel: string = 'Cancel Payment';

    @Input() isCheckoutSuccessPage: boolean = false;

    // True on an account settings page, the section Manage Profile opens. The other /profile
    // pages are rail destinations of their own, so a /profile prefix match would light it there.
    @Input() accountSettingsPage: boolean = false;

    // True while the navigation rail is on screen. Its Online Services item then owns the home
    // page and the pay flow, so Home and the page links leave the current marker to it.
    @Input() railShowing: boolean = false;

    onClick_signOut = async () => {
        if (this.preview || !this.authService) return;
        await this.authService.signOut('/sign-in');
    };

    onClick_toggleMobileMenu() {
        if (this.preview) return;
        this.toggleMobileMenu.emit(true);
    }

    onClick_toggleHelp() {
        if (this.preview) return;
        if (!this.appService?.currentUrl.includes('help')) this.toggleHelp.emit(true);
    }

    onClick_toggleCart() {
        if (this.preview) return;
        this.toggleCart.emit(true);
    }

    onClick_toggleAccountMenu() {
        if (this.preview) return;
        this.toggleAccountMenu.emit(true);
    }

    /** Initials for the signed-in avatar monogram (first + last word of the user's name). */
    get userInitials(): string {
        const name = (this.userService?.currentUser?.name || 'User').trim();
        const parts = name.split(/\s+/);
        const first = parts[0].charAt(0);
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

        return (first + last).toUpperCase();
    }

    /** Accessible name for the cart toggle; the badge sits on an aria-hidden mat-icon, so the
     * item count is announced here instead. */
    get cartToggleAriaLabel(): string {
        if (this.cartItemCount < 1) return 'Toggle cart';

        const noun = this.cartItemCount === 1 ? 'item' : 'items';
        return `Toggle cart, ${this.cartItemCount} ${noun} in cart`;
    }

    async onClick_cancelOrCompletePayment() {
        if (this.preview || !this.inboundRedirectService) return;
        if (this.isCheckoutSuccessPage) {
            await this.inboundRedirectService.promptForRedirectToSource();
        } else {
            await this.inboundRedirectService.cancelPaymentAndRedirect();
        }
    }

    /** Preview renders the site as configured rather than as flagged, so the editor still sees
     * every page-nav entry. */
    isPageUnderMaintenance(pageId: number): boolean {
        if (this.preview) return false;

        return this.siteMetadataService?.isPageUnderMaintenance(pageId) ?? false;
    }

    get canSavePaymentMethod(): boolean {
        return this.paymentProvider?.canSavePaymentMethod || false;
    }

    get logoSource(): string {
        if (this.logoSrc) {
            return this.logoSrc;
        }

        // Tenant logo comes from the bucket root via api/assets/*, so it is
        // track-independent. The default logo is a build-static asset.
        return this.siteMetadataService?.hasServableTenant
            ? `api/assets/${this.siteMetadataService.tenant}/logo.png`
            : 'assets/images/logo.png';
    }

    onLogoClick() {
        if (this.editMode) {
            this.editChange.emit({ field: 'logoClick', value: '' });
        }
    }
}
